import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import {
  AlertTriangle,
  BadgeEuro,
  Calculator,
  CheckCircle2,
  Clock3,
  FileSpreadsheet,
  Filter,
  Search,
  TrendingDown,
  TrendingUp,
  WalletCards,
  Wrench,
} from "lucide-react";
import { db } from "../firebase";
import { usePanelTheme } from "../context/themeContext";
import PageShell from "../components/PageShell";
import PanelCard from "../components/PanelCard";
import SectionHeading from "../components/SectionHeading";
import UIInput from "../components/UIInput";
import UISelect from "../components/UISelect";

const ui = {
  sq: {
    title: "Project profit tracking",
    subtitle: "Shiko fitimin e secilit projekt duke kombinuar buxhetin, ofertat dhe koston e punës.",
    totalProjectedProfit: "Fitimi i projektuar",
    profitableProjects: "Projekte fitimprurëse",
    watchlistProjects: "Në rrezik",
    avgMargin: "Marzhi mesatar",
    searchPlaceholder: "Kërko projekt, klient ose adresë",
    allStatuses: "Të gjitha statuset",
    allMargins: "Te Gjitha Projektet",
    positiveMargin: "Marzh pozitiv",
    lowMargin: "Marzh i ulët",
    negativeMargin: "Marzh negativ",
    projects: "Projekte",
    projectsSubtitle: "Përmbledhje e fitimit sipas projektit.",
    noProjects: "Nuk ka projekte për profit tracking",
    noProjectsText: "Shto projekte, oferta ose orë pune dhe do të shfaqen këtu.",
    status: "Statusi",
    planned: "Planned",
    in_progress: "In Progress",
    completed: "Finished",
    paused: "Paused",
    virtual: "Unlinked",
    revenueForecast: "Parashikim i të ardhurave",
    approvedOffers: "Oferta të miratuara",
    pendingOffers: "Oferta në pritje",
    totalCost: "Kosto totale",
    profit: "Fitimi",
    margin: "Marzhi",
    hours: "Orë",
    details: "Detaje projekti",
    client: "Klienti",
    address: "Adresa",
    laborSource: "Burimi i kostos së punës",
    timesheetLabor: "Nga timesheets",
    manualLabor: "Manual nga projekti",
    projectBudget: "Buxheti i projektit",
    approvedRevenue: "Të ardhura nga ofertat e miratuara",
    pendingRevenue: "Vlerë në pritje",
    laborCost: "Kosto pune",
    materialCost: "Kosto materiali",
    otherCost: "Kosto tjera",
    trackedHours: "Orë të regjistruara",
    totalOffers: "Gjithsej oferta",
    approvedCount: "Të miratuara",
    pendingCount: "Në pritje",
    recommendations: "Insight",
    healthy: "Ky projekt duket i shëndetshëm financiarisht.",
    low: "Marzhi është i ulët. Kontrollo kostot ose rrit çmimin final.",
    risk: "Ky projekt po del me humbje. Shiko menjëherë ofertat, buxhetin dhe koston e punës.",
    loadError: "Gabim gjatë leximit të profit tracking.",
    emptySelection: "Zgjidh një projekt për me pa breakdown-in e plotë.",
    countProjects: "projekte të analizuara",
    cardsNote: "Llogaritja përdor buxhetin e projektit ose ofertat e miratuara si burim të të ardhurave.",
    revenueRule: "Rregulli i të ardhurave",
    revenueRuleText: "Nëse projekti ka buxhet, përdoret ai. Përndryshe përdoren ofertat e miratuara.",
    linkedInvoices: "Fatura të lidhura",
    invoiceRevenue: "Të ardhura nga faturat",
  },
  de: {
    title: "Projekt Gewinntracking",
    subtitle: "Verfolge den Gewinn je Projekt mit Budget, Angeboten und Arbeitskosten.",
    totalProjectedProfit: "Prognostizierter Gewinn",
    profitableProjects: "Profitable Projekte",
    watchlistProjects: "Achtung",
    avgMargin: "Durchschnittsmarge",
    searchPlaceholder: "Projekt, Kunde oder Adresse suchen",
    allStatuses: "Alle Status",
    allMargins: "Alle Projekte",
    positiveMargin: "Positive Marge",
    lowMargin: "Niedrige Marge",
    negativeMargin: "Negative Marge",
    projects: "Projekte",
    projectsSubtitle: "Gewinnübersicht nach Projekt.",
    noProjects: "Keine Projekte für Gewinntracking",
    noProjectsText: "Lege Projekte, Angebote oder Arbeitszeiten an und sie erscheinen hier.",
    status: "Status",
    planned: "Geplant",
    in_progress: "In Arbeit",
    completed: "Abgeschlossen",
    paused: "Pausiert",
    virtual: "Nicht verknüpft",
    revenueForecast: "Umsatzprognose",
    approvedOffers: "Freigegebene Angebote",
    pendingOffers: "Offene Angebote",
    totalCost: "Gesamtkosten",
    profit: "Gewinn",
    margin: "Marge",
    hours: "Std.",
    details: "Projektdetails",
    client: "Kunde",
    address: "Adresse",
    laborSource: "Quelle der Arbeitskosten",
    timesheetLabor: "Aus Arbeitszeiten",
    manualLabor: "Manuell aus Projekt",
    projectBudget: "Projektbudget",
    approvedRevenue: "Freigegebener Angebotsumsatz",
    pendingRevenue: "Offener Angebotswert",
    laborCost: "Arbeitskosten",
    materialCost: "Materialkosten",
    otherCost: "Sonstige Kosten",
    trackedHours: "Erfasste Stunden",
    totalOffers: "Angebote gesamt",
    approvedCount: "Freigegeben",
    pendingCount: "Offen",
    recommendations: "Insight",
    healthy: "Dieses Projekt sieht finanziell gesund aus.",
    low: "Die Marge ist niedrig. Prüfe Kosten oder erhöhe den Endpreis.",
    risk: "Dieses Projekt läuft ins Minus. Prüfe sofort Angebote, Budget und Arbeitskosten.",
    loadError: "Fehler beim Laden des Gewinntrackings.",
    emptySelection: "Wähle ein Projekt aus, um die volle Aufschlüsselung zu sehen.",
    countProjects: "analysierte Projekte",
    cardsNote: "Die Berechnung nutzt das Projektbudget oder freigegebene Angebote als Umsatzquelle.",
    revenueRule: "Umsatzregel",
    revenueRuleText: "Wenn ein Projekt ein Budget hat, wird dieses verwendet. Sonst werden freigegebene Angebote genutzt.",
    linkedInvoices: "Verknüpfte Rechnungen",
    invoiceRevenue: "Rechnungsumsatz",
  },
};

const statusOptions = ["all", "planned", "in_progress", "completed", "paused", "virtual"];
const marginOptions = ["all", "positive", "low", "negative"];

function n(value) {
  return Number(value || 0);
}

function normalizedName(value) {
  return String(value || "").trim().toLowerCase();
}

function formatMoney(value, lang) {
  const locale = lang === "de" ? "de-DE" : "sq-AL";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(n(value));
}

function formatPercent(value, lang) {
  const locale = lang === "de" ? "de-DE" : "sq-AL";
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(n(value))}%`;
}

function statusTone(item) {
  if (item === "completed") return "emerald";
  if (item === "in_progress") return "blue";
  if (item === "paused") return "amber";
  if (item === "virtual") return "slate";
  return "violet";
}

function marginTone(value) {
  if (value < 0) return "rose";
  if (value < 15) return "amber";
  return "emerald";
}

function resolveToneClass(tone, theme, kind = "soft") {
  const palette = {
    emerald: theme === "dark"
      ? { soft: "border-emerald-500/20 bg-emerald-500/12 text-emerald-300", text: "text-emerald-300" }
      : { soft: "border-emerald-200 bg-emerald-50/80 text-emerald-700", text: "text-emerald-700" },
    blue: theme === "dark"
      ? { soft: "border-blue-500/20 bg-blue-500/12 text-blue-300", text: "text-blue-300" }
      : { soft: "border-blue-200 bg-blue-50/80 text-blue-700", text: "text-blue-700" },
    amber: theme === "dark"
      ? { soft: "border-amber-500/20 bg-amber-500/12 text-amber-300", text: "text-amber-300" }
      : { soft: "border-amber-200 bg-amber-50/80 text-amber-700", text: "text-amber-700" },
    rose: theme === "dark"
      ? { soft: "border-rose-500/20 bg-rose-500/12 text-rose-300", text: "text-rose-300" }
      : { soft: "border-rose-200 bg-rose-50/80 text-rose-700", text: "text-rose-700" },
    violet: theme === "dark"
      ? { soft: "border-violet-500/20 bg-violet-500/12 text-violet-300", text: "text-violet-300" }
      : { soft: "border-violet-200 bg-violet-50/80 text-violet-700", text: "text-violet-700" },
    slate: theme === "dark"
      ? { soft: "border-slate-700 bg-slate-900/70 text-slate-300", text: "text-slate-300" }
      : { soft: "border-slate-200 bg-slate-100/90 text-slate-700", text: "text-slate-700" },
  };
  return palette[tone]?.[kind] || palette.slate[kind];
}

function calcHours(entry) {
  const start = String(entry.startTime || "").split(":").map(Number);
  const end = String(entry.endTime || "").split(":").map(Number);
  if (start.length !== 2 || end.length !== 2 || start.some(Number.isNaN) || end.some(Number.isNaN)) return 0;
  const startMinutes = start[0] * 60 + start[1];
  const endMinutes = end[0] * 60 + end[1];
  const breakMinutes = n(entry.breakMinutes);
  const worked = endMinutes - startMinutes - breakMinutes;
  return worked > 0 ? Number((worked / 60).toFixed(2)) : 0;
}

function normalizeProject(docSnap) {
  const data = docSnap.data() || {};
  return {
    id: docSnap.id,
    name: data.name || "",
    clientName: data.clientName || "",
    address: data.address || "",
    status: data.status || "planned",
    budget: n(data.budget),
    laborCost: n(data.laborCost),
    materialCost: n(data.materialCost),
    otherCost: n(data.otherCost),
    totalHours: n(data.totalHours),
    notes: data.notes || "",
    startDate: data.startDate || "",
    endDate: data.endDate || "",
    virtual: false,
  };
}

function normalizeOffer(docSnap) {
  const data = docSnap.data() || {};
  return {
    id: docSnap.id,
    projectId: data.projectId || "",
    projectName: data.projectName || "",
    status: data.status || "draft",
    total: n(data.total),
  };
}

function normalizeInvoice(docSnap) {
  const data = docSnap.data() || {};
  return {
    id: docSnap.id,
    projectId: data.projectId || "",
    projectName: data.projectName || "",
    clientId: data.clientId || "",
    clientName: data.clientName || data.client?.name || "",
    total: n(data.total || data.grandTotal || data.amount),
  };
}

function normalizeTimesheet(docSnap) {
  const data = docSnap.data() || {};
  const hours = n(data.hours) || calcHours(data);
  const hourlyRate = n(data.hourlyRate);
  return {
    id: docSnap.id,
    projectId: data.projectId || "",
    projectName: data.projectName || "",
    hours,
    cost: Number((hours * hourlyRate).toFixed(2)),
  };
}

function findProjectKey({ projectId, projectName }, byId, byName) {
  if (projectId && byId.has(projectId)) return byId.get(projectId);
  const normalized = normalizedName(projectName);
  if (normalized && byName.has(normalized)) return byName.get(normalized);
  if (projectId) return `virtual:${projectId}`;
  if (normalized) return `virtual-name:${normalized}`;
  return null;
}


function normalizeMaterial(docSnap) {
  const data = docSnap.data() || {};
  const explicit = n(data.total || data.value || data.totalValue);
  const totalValue = explicit > 0 ? explicit : Number((n(data.quantity) * n(data.price)).toFixed(2));
  return {
    id: docSnap.id,
    projectId: data.projectId || "",
    projectName: data.projectName || "",
    name: data.name || "",
    quantity: n(data.quantity),
    price: n(data.price),
    totalValue,
  };
}

function normalizeExpense(docSnap) {
  const data = docSnap.data() || {};
  return {
    id: docSnap.id,
    projectId: data.projectId || "",
    projectName: data.projectName || "",
    amount: n(data.amount),
    category: data.category || "Other",
    description: data.description || "",
    date: data.date || "",
  };
}

function buildProfitRows(projects, offers, timesheets, invoices, materials, expenses) {

  const projectBuckets = new Map();
  const byId = new Map();
  const byName = new Map();

  projects.forEach((project) => {
    const key = `project:${project.id}`;
    byId.set(project.id, key);
    if (normalizedName(project.name)) byName.set(normalizedName(project.name), key);
    projectBuckets.set(key, {
      ...project,
      sourceKey: key,
      offersTotal: 0,
      offersApproved: 0,
      offersPending: 0,
      offersApprovedCount: 0,
      offersPendingCount: 0,
      timesheetHours: 0,
      timesheetCost: 0,
      materialsLinkedCost: 0,
      materialExpenseCost: 0,
      salaryExpenseCost: 0,
      otherExpenseCost: 0,
      materialsLinkedCount: 0,
      expenseCount: 0,
    });
  });

  const ensureVirtual = (item) => {
    const key = findProjectKey(item, byId, byName);
    if (!key) return null;
    if (!projectBuckets.has(key)) {
      projectBuckets.set(key, {
        id: key,
        name: item.projectName || "Unlinked project",
        clientName: "",
        address: "",
        status: "virtual",
        budget: 0,
        laborCost: 0,
        materialCost: 0,
        otherCost: 0,
        totalHours: 0,
        notes: "",
        startDate: "",
        endDate: "",
        virtual: true,
        sourceKey: key,
        offersTotal: 0,
        offersApproved: 0,
        offersPending: 0,
        offersApprovedCount: 0,
        offersPendingCount: 0,
        timesheetHours: 0,
        timesheetCost: 0,
        invoiceRevenue: 0,
        invoiceCount: 0,
        materialsLinkedCost: 0,
        materialExpenseCost: 0,
        salaryExpenseCost: 0,
        otherExpenseCost: 0,
        materialsLinkedCount: 0,
        expenseCount: 0,
      });
    }
    return key;
  };

  offers.forEach((offer) => {
    const key = ensureVirtual(offer);
    if (!key) return;
    const bucket = projectBuckets.get(key);
    bucket.offersTotal += 1;
    if (offer.status === "approved") {
      bucket.offersApproved += n(offer.total);
      bucket.offersApprovedCount += 1;
    } else if (offer.status === "sent" || offer.status === "draft") {
      bucket.offersPending += n(offer.total);
      bucket.offersPendingCount += 1;
    }
  });

  invoices.forEach((invoice) => {
    const key = ensureVirtual(invoice);
    if (!key) return;
    const bucket = projectBuckets.get(key);
    bucket.invoiceRevenue += n(invoice.total);
    bucket.invoiceCount += 1;
  });

  timesheets.forEach((entry) => {
    const key = ensureVirtual(entry);
    if (!key) return;
    const bucket = projectBuckets.get(key);
    bucket.timesheetHours += n(entry.hours);
    bucket.timesheetCost += n(entry.cost);
  });

  materials.forEach((material) => {
    const key = ensureVirtual(material);
    if (!key) return;
    const bucket = projectBuckets.get(key);
    bucket.materialsLinkedCost += n(material.totalValue || (n(material.quantity) * n(material.price)));
    bucket.materialsLinkedCount += 1;
  });

  expenses.forEach((expense) => {
    const key = ensureVirtual(expense);
    if (!key) return;
    const bucket = projectBuckets.get(key);
    const category = normalizedName(expense.category);
    const amount = n(expense.amount);
    if (category === "material") {
      bucket.materialExpenseCost += amount;
    } else if (category === "salary") {
      bucket.salaryExpenseCost += amount;
    } else {
      bucket.otherExpenseCost += amount;
    }
    bucket.expenseCount += 1;
  });

  return Array.from(projectBuckets.values()).map((item) => {
    const laborSource = item.timesheetCost > 0 ? "timesheet" : item.salaryExpenseCost > 0 ? "salary" : "manual";
    const laborUsed = laborSource === "timesheet" ? item.timesheetCost : laborSource === "salary" ? item.salaryExpenseCost : item.laborCost;
    const materialSource = item.materialsLinkedCost > 0 ? "materials" : item.materialExpenseCost > 0 ? "material_expenses" : "manual";
    const materialUsed = materialSource === "materials" ? item.materialsLinkedCost : materialSource === "material_expenses" ? item.materialExpenseCost : item.materialCost;
    const otherUsed = item.otherExpenseCost > 0 ? item.otherExpenseCost : item.otherCost;
    const totalCost = laborUsed + materialUsed + otherUsed;
    const revenueForecast = item.budget > 0 ? item.budget : (item.offersApproved > 0 ? item.offersApproved : item.invoiceRevenue);
    const profit = revenueForecast - totalCost;
    const margin = revenueForecast > 0 ? Number(((profit / revenueForecast) * 100).toFixed(1)) : 0;
    return {
      ...item,
      laborSource,
      laborUsed,
      materialSource,
      materialUsed,
      otherUsed,
      totalCost,
      revenueForecast,
      profit,
      margin,
      riskLevel: profit < 0 ? "negative" : margin < 15 ? "low" : "positive",
    };
  });
}

function MiniMetric({ label, value, theme, strong = false, tone = "slate" }) {
  return (
    <div className={`rounded-2xl border px-4 py-3 ${resolveToneClass(tone, theme, "soft")}`}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] opacity-75">{label}</div>
      <div className={`mt-1 text-sm ${strong ? "font-black" : "font-semibold"}`}>{value}</div>
    </div>
  );
}

function StatCard({ icon: Icon, title, value, subtitle, theme, tone = "slate" }) {
  const iconTone =
    tone === "emerald"
      ? theme === "dark" ? "bg-emerald-500/18 text-emerald-300" : "bg-emerald-100 text-emerald-700"
      : tone === "blue"
        ? theme === "dark" ? "bg-blue-500/18 text-blue-300" : "bg-blue-100 text-blue-700"
        : tone === "amber"
          ? theme === "dark" ? "bg-amber-500/18 text-amber-300" : "bg-amber-100 text-amber-700"
          : tone === "rose"
            ? theme === "dark" ? "bg-rose-500/18 text-rose-300" : "bg-rose-100 text-rose-700"
            : tone === "violet"
              ? theme === "dark" ? "bg-violet-500/18 text-violet-300" : "bg-violet-100 text-violet-700"
              : theme === "dark"
                ? "bg-slate-900 text-slate-300"
                : "bg-white/80 text-slate-700";

  return (
    <PanelCard className={tone === "slate" ? "" : resolveToneClass(tone, theme, "soft")}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold opacity-80">{title}</p>
          <div className="mt-3 text-3xl sm:text-4xl font-black tracking-tight">{value}</div>
          {subtitle ? <p className="mt-2 text-xs font-medium opacity-70">{subtitle}</p> : null}
        </div>
        <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${iconTone}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </PanelCard>
  );
}

function DetailRow({ label, value, theme }) {
  return (
    <div className={`flex items-center justify-between gap-4 border-b py-3 text-sm ${theme === "dark" ? "border-slate-800 text-slate-300" : "border-slate-100 text-slate-700"}`}>
      <span className="font-medium opacity-80">{label}</span>
      <span className="text-right font-semibold">{value}</span>
    </div>
  );
}

export default function ProjectProfit({ lang = "sq", t = {} }) {
  const text = ui[lang] || ui.sq;
  const { theme, currentTheme } = usePanelTheme();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [marginFilter, setMarginFilter] = useState("all");
  const [projects, setProjects] = useState([]);
  const [offers, setOffers] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [selectedKey, setSelectedKey] = useState("");

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        const [projectSnap, offerSnap, timesheetSnap, invoiceSnap, materialSnap, expenseSnap] = await Promise.all([
          getDocs(collection(db, "projects")),
          getDocs(collection(db, "offers")),
          getDocs(collection(db, "timesheets")),
          getDocs(collection(db, "invoices")),
          getDocs(collection(db, "materials")),
          getDocs(collection(db, "expenses")),
        ]);
        if (!active) return;
        setProjects(projectSnap.docs.map(normalizeProject));
        setOffers(offerSnap.docs.map(normalizeOffer));
        setTimesheets(timesheetSnap.docs.map(normalizeTimesheet));
        setInvoices(invoiceSnap.docs.map(normalizeInvoice));
        setMaterials(materialSnap.docs.map(normalizeMaterial));
        setExpenses(expenseSnap.docs.map(normalizeExpense));
      } catch (error) {
        console.error(text.loadError, error);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [text.loadError]);

  const rows = useMemo(() => buildProfitRows(projects, offers, timesheets, invoices, materials, expenses), [projects, offers, timesheets, invoices, materials, expenses]);

  const filteredRows = useMemo(() => {
    const term = normalizedName(search);
    return rows.filter((item) => {
      const statusOk = statusFilter === "all" ? true : item.status === statusFilter;
      const marginOk = marginFilter === "all" ? true : item.riskLevel === marginFilter;
      const haystack = [item.name, item.clientName, item.address].join(" ").toLowerCase();
      const searchOk = term ? haystack.includes(term) : true;
      return statusOk && marginOk && searchOk;
    }).sort((a, b) => b.profit - a.profit);
  }, [rows, search, statusFilter, marginFilter]);

  useEffect(() => {
    if (!filteredRows.length) {
      setSelectedKey("");
      return;
    }
    const exists = filteredRows.some((item) => item.sourceKey === selectedKey);
    if (!exists) setSelectedKey(filteredRows[0].sourceKey);
  }, [filteredRows, selectedKey]);

  const selected = filteredRows.find((item) => item.sourceKey === selectedKey) || filteredRows[0] || rows[0] || null;

  const stats = useMemo(() => {
    const totalProjectedProfit = rows.reduce((sum, item) => sum + item.profit, 0);
    const profitableProjects = rows.filter((item) => item.profit > 0).length;
    const watchlistProjects = rows.filter((item) => item.profit <= 0 || item.margin < 15).length;
    const avgMargin = rows.length ? rows.reduce((sum, item) => sum + item.margin, 0) / rows.length : 0;
    return { totalProjectedProfit, profitableProjects, watchlistProjects, avgMargin };
  }, [rows]);

  const statusLabel = (value) => {
    if (value === "all") return text.allStatuses;
    return text[value] || value;
  };

  const marginLabel = (value) => {
    if (value === "all") return text.allMargins;
    if (value === "positive") return text.positiveMargin;
    if (value === "low") return text.lowMargin;
    if (value === "negative") return text.negativeMargin;
    return value;
  };

  const insight = (item) => {
    if (!item) return text.emptySelection;
    if (item.profit < 0) return text.risk;
    if (item.margin < 15) return text.low;
    return text.healthy;
  };

  return (
    <PageShell>
      <SectionHeading title={text.title} subtitle={text.subtitle} />

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatCard icon={BadgeEuro} title={text.totalProjectedProfit} value={formatMoney(stats.totalProjectedProfit, lang)} subtitle={text.cardsNote} theme={theme} tone={stats.totalProjectedProfit >= 0 ? "emerald" : "rose"} />
        <StatCard icon={TrendingUp} title={text.profitableProjects} value={String(stats.profitableProjects)} subtitle={text.countProjects} theme={theme} tone="blue" />
        <StatCard icon={AlertTriangle} title={text.watchlistProjects} value={String(stats.watchlistProjects)} subtitle={rows.length ? text.projectsSubtitle : text.noProjectsText} theme={theme} tone={stats.watchlistProjects ? "amber" : "emerald"} />
        <StatCard icon={Calculator} title={text.avgMargin} value={formatPercent(stats.avgMargin, lang)} subtitle={text.revenueRuleText} theme={theme} tone={stats.avgMargin >= 15 ? "emerald" : "amber"} />
      </div>

      <PanelCard>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className={`text-lg font-black ${currentTheme.text}`}>{text.projects}</h3>
            <p className={`mt-1 text-sm ${currentTheme.muted}`}>{text.projectsSubtitle}</p>
          </div>
          <div className="grid w-full gap-3 sm:grid-cols-2 lg:w-auto lg:min-w-[720px] lg:grid-cols-[minmax(260px,1fr)_180px_180px]">
            <div className="relative">
              <Search className={`pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 ${currentTheme.muted}`} />
              <UIInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder={text.searchPlaceholder} className="pl-11" />
            </div>
            <div className="relative">
              <Filter className={`pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 ${currentTheme.muted}`} />
              <UISelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="pl-11">
                {statusOptions.map((option) => (
                  <option key={option} value={option}>{statusLabel(option)}</option>
                ))}
              </UISelect>
            </div>
            <div className="relative">
              <Wrench className={`pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 ${currentTheme.muted}`} />
              <UISelect value={marginFilter} onChange={(e) => setMarginFilter(e.target.value)} className="pl-11">
                {marginOptions.map((option) => (
                  <option key={option} value={option}>{marginLabel(option)}</option>
                ))}
              </UISelect>
            </div>
          </div>
        </div>
      </PanelCard>

      <div className="grid gap-6 2xl:grid-cols-[minmax(0,1.35fr)_400px]">
        <PanelCard>
          {loading ? (
            <div className={`rounded-3xl border border-dashed px-6 py-14 text-center text-sm font-semibold ${theme === "dark" ? "border-slate-800 text-slate-400" : "border-slate-200 text-slate-500"}`}>{t.loading || "Loading..."}</div>
          ) : filteredRows.length ? (
            <div className="space-y-4">
              {filteredRows.map((item) => {
                const statTone = marginTone(item.margin);
                return (
                  <button
                    type="button"
                    key={item.sourceKey}
                    onClick={() => setSelectedKey(item.sourceKey)}
                    className={`w-full rounded-[26px] border p-4 text-left transition ${selected?.sourceKey === item.sourceKey ? (theme === "dark" ? "border-blue-500/40 bg-blue-500/5" : "border-blue-300 bg-blue-50/70") : (theme === "dark" ? "border-slate-800 bg-slate-950/50 hover:border-slate-700" : "border-slate-200 bg-slate-50/70 hover:border-slate-300")}`}
                  >
                    <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className={`truncate text-lg font-black ${currentTheme.text}`}>{item.name || "—"}</h4>
                          <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.18em] ${resolveToneClass(statusTone(item.status), theme, "soft")}`}>
                            {statusLabel(item.status)}
                          </span>
                          <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.18em] ${resolveToneClass(statTone, theme, "soft")}`}>
                            {marginLabel(item.riskLevel)}
                          </span>
                        </div>
                        <div className={`mt-1 text-sm ${currentTheme.muted}`}>
                          {item.clientName || "—"}
                          {item.address ? ` • ${item.address}` : ""}
                        </div>

                        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                          <MiniMetric label={text.revenueForecast} value={formatMoney(item.revenueForecast, lang)} theme={theme} tone="blue" />
                          <MiniMetric label={text.totalCost} value={formatMoney(item.totalCost, lang)} theme={theme} tone="slate" />
                          <MiniMetric label={text.profit} value={formatMoney(item.profit, lang)} theme={theme} tone={statTone} strong />
                          <MiniMetric label={text.margin} value={formatPercent(item.margin, lang)} theme={theme} tone={statTone} />
                          <MiniMetric label={text.hours} value={new Intl.NumberFormat(lang === "de" ? "de-DE" : "sq-AL", { maximumFractionDigits: 1 }).format(item.timesheetHours || item.totalHours || 0)} theme={theme} tone="violet" />
                        </div>
                      </div>

                      <div className={`flex min-w-[180px] flex-col rounded-[22px] border px-4 py-4 ${theme === "dark" ? "border-slate-800 bg-slate-900/60" : "border-slate-200 bg-white"}`}>
                        <div className="flex items-center gap-2 text-sm font-semibold">
                          <WalletCards className="h-4 w-4" />
                          {text.revenueRule}
                        </div>
                        <div className={`mt-2 text-xs leading-5 ${currentTheme.muted}`}>{item.budget > 0 ? text.projectBudget : text.approvedRevenue}</div>
                        <div className={`mt-3 text-2xl font-black ${resolveToneClass(statTone, theme, "text")}`}>
                          {formatMoney(item.profit, lang)}
                        </div>
                        <div className={`mt-1 text-xs font-medium ${currentTheme.muted}`}>{insight(item)}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className={`rounded-3xl border border-dashed px-6 py-14 text-center ${theme === "dark" ? "border-slate-800" : "border-slate-200"}`}>
              <div className={`text-lg font-black ${currentTheme.text}`}>{text.noProjects}</div>
              <p className={`mx-auto mt-2 max-w-md text-sm ${currentTheme.muted}`}>{text.noProjectsText}</p>
            </div>
          )}
        </PanelCard>

        <PanelCard>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className={`text-lg font-black ${currentTheme.text}`}>{text.details}</h3>
              <p className={`mt-1 text-sm ${currentTheme.muted}`}>{selected ? selected.name : text.emptySelection}</p>
            </div>
            <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${theme === "dark" ? "bg-slate-900 text-slate-200" : "bg-slate-100/90 text-slate-700"}`}>
              <FileSpreadsheet className="h-5 w-5" />
            </div>
          </div>

          {selected ? (
            <div className="mt-5 space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <MiniMetric label={text.profit} value={formatMoney(selected.profit, lang)} theme={theme} tone={marginTone(selected.margin)} strong />
                <MiniMetric label={text.margin} value={formatPercent(selected.margin, lang)} theme={theme} tone={marginTone(selected.margin)} />
              </div>

              <div>
                <DetailRow label={text.client} value={selected.clientName || "—"} theme={theme} />
                <DetailRow label={text.address} value={selected.address || "—"} theme={theme} />
                <DetailRow label={text.projectBudget} value={formatMoney(selected.budget, lang)} theme={theme} />
                <DetailRow label={text.approvedRevenue} value={formatMoney(selected.offersApproved, lang)} theme={theme} />
                <DetailRow label={text.pendingRevenue} value={formatMoney(selected.offersPending, lang)} theme={theme} />
                <DetailRow label={text.invoiceRevenue} value={formatMoney(selected.invoiceRevenue, lang)} theme={theme} />
                <DetailRow label={text.laborSource} value={selected.laborSource === "timesheet" ? text.timesheetLabor : selected.laborSource === "salary" ? (lang === "de" ? "Aus Ausgaben (Lohn)" : "Nga shpenzimet (pagë)") : text.manualLabor} theme={theme} />
                <DetailRow label={lang === "de" ? "Materialquelle" : "Burimi i materialit"} value={selected.materialSource === "materials" ? (lang === "de" ? "Aus Materialien" : "Nga materialet") : selected.materialSource === "material_expenses" ? (lang === "de" ? "Aus Ausgaben (Material)" : "Nga shpenzimet (material)") : (lang === "de" ? "Manuell aus Projekt" : "Manual nga projekti")} theme={theme} />
                <DetailRow label={text.laborCost} value={formatMoney(selected.laborUsed, lang)} theme={theme} />
                <DetailRow label={text.materialCost} value={formatMoney(selected.materialUsed, lang)} theme={theme} />
                <DetailRow label={text.otherCost} value={formatMoney(selected.otherUsed, lang)} theme={theme} />
                <DetailRow label={text.totalCost} value={formatMoney(selected.totalCost, lang)} theme={theme} />
                <DetailRow label={text.trackedHours} value={new Intl.NumberFormat(lang === "de" ? "de-DE" : "sq-AL", { maximumFractionDigits: 1 }).format(selected.timesheetHours || selected.totalHours || 0)} theme={theme} />
                <DetailRow label={text.totalOffers} value={String(selected.offersTotal)} theme={theme} />
                <DetailRow label={text.linkedInvoices} value={String(selected.invoiceCount || 0)} theme={theme} />
                <DetailRow label={text.approvedCount} value={String(selected.offersApprovedCount)} theme={theme} />
                <DetailRow label={text.pendingCount} value={String(selected.offersPendingCount)} theme={theme} />
              </div>

              <div className={`rounded-[24px] border p-4 ${resolveToneClass(marginTone(selected.margin), theme, "soft")}`}>
                <div className="flex items-center gap-2 text-sm font-bold">
                  {selected.margin < 0 ? <TrendingDown className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                  {text.recommendations}
                </div>
                <p className="mt-2 text-sm leading-6">{insight(selected)}</p>
              </div>

              <div className={`rounded-[24px] border p-4 ${theme === "dark" ? "border-slate-800 bg-slate-900/60" : "border-slate-200 bg-slate-50/80"}`}>
                <div className="flex items-center gap-2 text-sm font-bold">
                  <Clock3 className="h-4 w-4" />
                  {text.revenueRule}
                </div>
                <p className={`mt-2 text-sm leading-6 ${currentTheme.muted}`}>{text.revenueRuleText}</p>
              </div>
            </div>
          ) : (
            <div className={`mt-6 rounded-[24px] border border-dashed px-5 py-10 text-center text-sm ${theme === "dark" ? "border-slate-800 text-slate-400" : "border-slate-200 text-slate-500"}`}>{text.emptySelection}</div>
          )}
        </PanelCard>
      </div>
    </PageShell>
  );
}
